import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { KINETIC_WORD_SETTING } from './kineticWordSetting';
import { check, treatsSeveral } from '../skillUsage';

export const wordFromFragmentsSkill: CodeoSkill = {
    name: SKILL_NAMES.wordFromFragments,
    kind: 'codeo',
    description:
        'Assemble a word from square fragments of itself that fly in tumbling from a scattered ' +
        'cluster and click into place, so the word is built in front of the viewer — for a word ' +
        'about building, assembling, fixing, connecting, snapping together or pieces.',
    checkLooksFor:
        'a grid of tiles each cropping its own copy of the word (overflow: hidden), tweened in from scattered, rotated starts',
    documentUsesSkill: check(
        (script, markup, html) =>
            /overflow:\s*hidden|overflow\s*=\s*['"]hidden|overflow\s*:\s*['"]hidden/.test(html) &&
            /cloneNode|innerHTML|createElement|insertAdjacentHTML/.test(script) &&
            /rotation/.test(script) &&
            treatsSeveral(script),
    ),
    reviewNote:
        'Square pieces of letters scattered and rotated, partly assembled into a word, are this ' +
        'technique mid-build: the fragments are the word, not broken rendering.',
    instructions: `# Word From Fragments

The word is cut into a grid of square tiles, each one a window onto its own patch of the word. At the start the tiles are **huddled in a scattered cluster** near the middle, small and spun at odd angles. On the beat they **fly out to their places**, tumbling as they go, and **click into register** — the last few degrees of rotation snapping straight in the final frames, so the word locks together with a visible snap. It takes about a third of a second.

Use it for a word about building, assembling, fixing, connecting, snapping, pieces coming together.

${KINETIC_WORD_SETTING}

## Markup

A stage the size of the word, holding one hidden "master" line (for measuring) and the tiles the script builds. Each tile crops its own copy of the line.

\`\`\`html
<div class="absolute inset-0 flex items-center justify-center">
  <div id="frag-stage" style="position: relative;">
    <div id="frag-master" class="frag-word" data-l5-allow-literal>SNAP</div>
  </div>
</div>
\`\`\`

\`\`\`css
.frag-word { line-height: 1; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-weight: 800; text-transform: uppercase; color: var(--l5-color-3, #fafafa); padding: 0.06em 0.04em; }
#frag-master { visibility: hidden; }
.frag-tile { position: absolute; overflow: hidden; }
.frag-tile .frag-word { position: absolute; }
\`\`\`

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const master = document.querySelector('#frag-master');
const stage = document.querySelector('#frag-stage');
const CUE = 0.55;                               // the word snaps together on its cue
let tiles = [];

// Built again on every fit: how many tiles the word needs, and where each sits, depend on its
// proportions in the face it is finally drawn in.
function build() {
  tiles.forEach(({ tile }) => tile.remove());
  tl.clear();
  // A seeded random, so every render of the frame is the same.
  let seed = 7;
  const rand = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);

  const W = master.offsetWidth, H = master.offsetHeight;
  const TILE = Math.ceil(H / 4);                // four rows of tiles over the word's height
  tiles = [];
  for (let y = 0; y < H; y += TILE) {
    for (let x = 0; x < W; x += TILE) {
      const tile = document.createElement('div');
      tile.className = 'frag-tile';
      tile.style.cssText += 'left:' + x + 'px; top:' + y + 'px; width:' + TILE + 'px; height:' + TILE + 'px;';
      const copy = master.cloneNode(true);
      copy.removeAttribute('id');
      copy.setAttribute('aria-hidden', 'true');
      copy.setAttribute('data-l5-allow-clip', '');
      copy.style.left = -x + 'px';
      copy.style.top = -y + 'px';
      tile.appendChild(copy);
      stage.appendChild(tile);
      tiles.push({ tile, cx: x + TILE / 2 - W / 2, cy: y + TILE / 2 - H / 2 });
    }
  }

  tiles.forEach(({ tile, cx, cy }) => {
    const far = Math.hypot(cx, cy) / Math.hypot(W / 2, H / 2);   // 0 centre → 1 corner
    const at = CUE - 0.36 + far * 0.08 + rand() * 0.05;
    tl.fromTo(tile,
      { x: -cx * 0.7 + (rand() - 0.5) * 160, y: -cy * 0.6 + (rand() - 0.5) * 120,
        scale: 0.25, rotation: (rand() - 0.5) * 140 },
      { x: 0, y: 0, scale: 1, rotation: (rand() - 0.5) * 8, duration: 0.3, ease: 'power3.out' }, at);
    // The click: the last few degrees snap straight.
    tl.to(tile, { rotation: 0, duration: 0.05, ease: 'power4.in' }, CUE - 0.02 + rand() * 0.03);
  });
  tl.set({}, {}, 1.8);
}

fitWord(master, 0.84, build);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **Smaller pieces for more chaos.** Five or six rows of tiles over the word's height for "shatter" and "pieces"; three for a bolder, chunkier build.
- **Taking it apart.** Run it backwards for "break", "fall apart" or "fragmented": the tiles fly out from their places.
- **A sound on the click.** The snap frame is the beat; land it on the stressed syllable.

## Pitfalls

- **Hide the master by its id, never inline.** The copies are clones of it with the id removed, so a rule on \`#frag-master\` hides the master alone. A \`style="visibility: hidden"\` on the master is cloned into every copy, and an inline style beats any stylesheet rule that tries to show them: the whole word stays invisible.
- **Every tile shows its own patch.** The copy inside each tile is offset by minus the tile's position. Get the sign wrong and every tile shows the same corner of the word.
- **No gaps once built.** Tiles meet exactly; a hairline between them at rest means the tile size and the stepping disagree. Use the same \`TILE\` for both.
- **Built from the word's final size.** The tiles are cut from measurements of the fitted word, so they are cut again whenever it is refitted. Tiles cut once, before the video's font loads, show the wrong patches of the word, or leave its end uncovered.
- **Deterministic.** Use a seeded random as above, never \`Math.random()\`: a frame must render the same every time.
- **Fast.** The flight is about 0.3s. A slow assembly reads as a puzzle being solved, not a snap.`,
};
